import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
} from 'react';

export interface ReCaptchaContext {
    /**
     * Currently used siteKey or null, if none is set
     */
    siteKey: string | null;

    /**
     * Allows to update the site key.
     */
    setSiteKey: (siteKey: string | null) => void;

    /**
     * To be used to get captcha token for sending to the server
     * @returns one-time use captcha token or null, if no site key is in use
     */
    getCaptchaToken: () => Promise<string | null>;
}

/**
 * Context reference, with some bogus default values that are not going to be used anyway.
 */
const Context = createContext<ReCaptchaContext>({
    siteKey: null,
    setSiteKey: () => {},
    getCaptchaToken: () => Promise.resolve(null),
});

/**
 * State provider for @see ReCaptchaContext.
 * @param {object} props parameters
 * @param {Element} props.children inner elements
 * @returns {Element} JSX
 */
export const ReCaptchaProvider = (props: {
    children: JSX.Element | JSX.Element[];
}) => {
    const [siteKey, setSiteKey] = useState<string | null>(null);

    // setup the script
    useEffect(() => {
        const id = `reCAPTCHA`;
        if (siteKey) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
            script.id = id;
            script.async = true;
            document.body.appendChild(script);

            // cleanup
            return () => {
                window.grecaptcha.enterprise.ready(() => {
                    // remove badges - this seems to be the only way
                    const badges: Element[] = Array.from(
                        document.getElementsByClassName('grecaptcha-badge'),
                    );
                    badges.forEach((el) => el.remove());

                    document.body.removeChild(script);
                });
            };
        }
    }, [siteKey]);

    const getCaptchaToken = useCallback(async () => {
        // no required captcha should produces null token
        if (!siteKey) return null;

        await new Promise<void>((resolve) =>
            // ONEDAY: wait for script to load
            window.grecaptcha.enterprise.ready(resolve),
        );

        // ONEDAY: check if the site key was unloaded at this time due to
        // a premature navigation
        const token = await window.grecaptcha.enterprise.execute(siteKey, {
            action: 'submit',
        });

        return token;
    }, [siteKey]);

    const context: ReCaptchaContext = {
        siteKey,
        setSiteKey,
        getCaptchaToken,
    };

    return (
        <Context.Provider value={context}>{props.children}</Context.Provider>
    );
};

/**
 * Provides an API for Google reCaptcha interaction from within the page.
 * @param {string} siteKey reCaptcha site key or null
 * @returns {ReCaptchaContext} use this getCaptchaToken method
 */
export const useReCaptchaContext = (siteKey: string | undefined | null) => {
    const context = useContext(Context);

    useEffect(() => {
        context.setSiteKey(siteKey ?? null);
    }, [context, siteKey]);

    return context;
};
